import { useContext, useEffect, useRef } from 'react'
import { UserContext, UserContextType } from '../UserContext'
import ElectronIPC, { IpcWindowChangedEventData, WindowMetrics } from './ElectronIPC'
import { persistent } from './LocalStorage'
import useMiscRoomActions from './useMiscRoomActions'
import { MediaDeviceError } from './MediaDevices'
import useRoomContextRef from './useRoomContextRef'
import { env } from './EnvironmentVariables'
import useAppContextRef from './useAppContextRef'
import useUserContextRef from './useUserContextRef'
import { sessionViewModeState, useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'

export default function useCollaboratePageWindowPolicy() {
    const { state } = useContext(UserContext) as UserContextType

    const viewMode = useViewMode()

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const {
        setRetract,
        windowRetract,
        windowRevert,
        windowThinStripRight,
        windowThinStripRightForFullscreen,
        windowThinStripRevertForFullscreen,
    } = useMiscRoomActions()

    useEffect(() => {
        let isFirst = true
        const handler = async (data: IpcWindowChangedEventData) => {
            // Ignore first event
            if (isFirst) {
                isFirst = false
                return
            }

            if (data.windowName !== 'mainWindow') {
                return
            }

            const { eventName, windowMetrics } = data

            const isSidebarRetracted = viewMode.sidebarCollapsed
            const isRetracted = viewMode.galleryHidden
            const { isRetractedInProgress } = roomStateRef.current
            const isFullscreen = viewMode.fullscreen

            const isPresenting =
                userStateRef.current.mediaSelection.desktop !== '_none' &&
                userStateRef.current.mediaSelection.desktop !== '_undef' &&
                userStateRef.current.mediaStreams.desktop instanceof MediaStream

            console.log(
                `collaboratePageWindowBoundsChangedHandler: ` +
                    `eventName="${eventName}" ` +
                    `windowMetrics=${JSON.stringify(windowMetrics)} ` +
                    `isSidebarRetracted=${isSidebarRetracted} ` +
                    `isRetracted=${isRetracted} ` +
                    `isRetractedInProgress=${isRetractedInProgress} ` +
                    `isFullscreen=${viewMode.fullscreen} ` +
                    `wasRetractedBeforeFullscreen=${appStateRef.current.appState.wasRetractedBeforeFullscreen} ` +
                    `isPresenting=${isPresenting}`
            )

            // If the sidebar is retracted, we do not save any of the window metrics.
            if (viewMode.sidebarCollapsed) {
                return
            }

            if (isRetractedInProgress) {
                return
            }

            // ugly: sometimes, StartupPage.winmetrics gets applied and then the `registerWindowChangedHandler` gets
            // fired when exiting the room. we ignore this case.
            const startupPageWinMetrics = persistent.getObject('StartupPage.winmetrics')
            if (
                startupPageWinMetrics?.x === windowMetrics.x &&
                startupPageWinMetrics?.y === windowMetrics.y &&
                startupPageWinMetrics?.width === windowMetrics.width &&
                startupPageWinMetrics?.height === windowMetrics.height
            ) {
                return
            }

            // We only change the bounds when the window is not maximized; otherwise, we
            // end up storing the bounds of the window in its maximized state.
            if (eventName === 'bounds' && !isFullscreen && !windowMetrics.isMaximized && !windowMetrics.isFullScreen) {
                if (isRetracted) {
                    console.log(
                        `collaboratePageWindowBoundsChangedHandler: (case 1) saving 'CollaboratePage.winmetrics.retracted' ${JSON.stringify(windowMetrics)}`
                    )
                    persistent.set('CollaboratePage.winmetrics.retracted', windowMetrics)
                } else {
                    console.log(
                        `collaboratePageWindowBoundsChangedHandler: (case 2) saving 'CollaboratePage.winmetrics.normal' ${JSON.stringify(windowMetrics)}`
                    )
                    persistent.set('CollaboratePage.winmetrics.normal', windowMetrics)
                }
            }

            // We care about max/unmaximize only when the window state is normal,
            // hence the !isRetracted condition.
            if (!isRetracted && (eventName === 'maximized' || eventName === 'unmaximized')) {
                // Only set the 'isMaximized' property of the localStorage item
                // to `windowMetrics.isMaximized`
                let w = persistent.getObject('CollaboratePage.winmetrics.normal')
                if (w) {
                    w = w as WindowMetrics
                    persistent.set('CollaboratePage.winmetrics.normal', {
                        ...w,
                        isMaximized: windowMetrics.isMaximized,
                    })
                    console.log(
                        `collaboratePageWindowBoundsChangedHandler: (case 3) saving 'CollaboratePage.winmetrics.normal' ${JSON.stringify(windowMetrics)}`
                    )
                } else {
                    persistent.set('CollaboratePage.winmetrics.normal', windowMetrics)
                    console.log(
                        `collaboratePageWindowBoundsChangedHandler: (case 4) saving 'CollaboratePage.winmetrics.normal' ${JSON.stringify(windowMetrics)}`
                    )
                }
            }
        }
        ElectronIPC.registerWindowChangedHandler(handler)

        return () => {
            ElectronIPC.removeWindowChangedHandler(handler)
        }
    }, [])

    useEffect(() => {
        if (viewMode.fullscreen) {
            if (viewMode.sidebarCollapsed) {
                windowThinStripRightForFullscreen()
            } else {
                windowThinStripRevertForFullscreen()
            }
        } else {
            if (viewMode.galleryHidden) {
                if (viewMode.sidebarCollapsed) {
                    windowThinStripRight()
                } else {
                    windowRetract({
                        doNotRestoreSavedWindowDimension:
                            roomStateRef.current['isRetractedDoNotRestoreSavedWindowDimension'],
                    })
                }
            } else {
                windowRevert({
                    doNotRestoreSavedWindowDimension:
                        roomStateRef.current['isRetractedDoNotRestoreSavedWindowDimension'],
                })
            }
        }
    }, [
        roomStateRef,
        viewMode,
        windowRetract,
        windowRevert,
        windowThinStripRevertForFullscreen,
        windowThinStripRight,
        windowThinStripRightForFullscreen,
    ])

    // SECTION: isRetracted refactory period timer
    const isRetractedRefactoryPeriodTimer = useRef<NodeJS.Timeout | null>(null)
    useEffect(() => {
        if (isRetractedRefactoryPeriodTimer.current) {
            clearTimeout(isRetractedRefactoryPeriodTimer.current)
        }
        console.log(`autoGalleryRetract: isRetracted changed; setting isRetractedRefactoryPeriodTimer.`)
        isRetractedRefactoryPeriodTimer.current = setTimeout(() => {
            if (isRetractedRefactoryPeriodTimer.current) {
                console.log(
                    `autoGalleryRetract: clearing isRetractedRefactoryPeriodTimer because the ` +
                        `refractory period expired.`
                )
                clearTimeout(isRetractedRefactoryPeriodTimer.current)
                isRetractedRefactoryPeriodTimer.current = null
            }
        }, 500)
    }, [viewMode.galleryHidden])

    const isRetractedStateBeforeScreenShareRef = useRef(false)
    useEffect(() => {
        if (!env.IS_ELECTRON) {
            return
        }

        if (state.mediaStreams.desktop == null || state.mediaStreams.desktop instanceof MediaDeviceError) {
            if (isRetractedStateBeforeScreenShareRef.current === false) {
                sessionViewModeState.setGalleryHidden(false)
            }
        } else if (state.mediaStreams.desktop instanceof MediaStream) {
            isRetractedStateBeforeScreenShareRef.current = sessionViewModeState.mode.galleryHidden
            sessionViewModeState.setGalleryHidden(true)
        }
    }, [state.mediaStreams.desktop])
}
